import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { FaCarSide, FaMapMarkerAlt, FaPhoneAlt } from "react-icons/fa";
import Spinner from "./Components/Spinner";
import { getVehiclePath } from "./Utils/vehicleUrls";

export default function DealerInventory() {
  const { dealerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const view = searchParams.get("view") || "all";
  const sort = searchParams.get("sort") || "recent";
  const keyword = searchParams.get("keyword") || "";
  const minYear = searchParams.get("minYear") || "";
  const maxYear = searchParams.get("maxYear") || "";
  const [page, setPage] = useState(Number(searchParams.get("page") || 1));
  const [pageSize, setPageSize] = useState(Number(searchParams.get("pageSize") || 24));

  const [draftView, setDraftView] = useState(view);
  const [draftSort, setDraftSort] = useState(sort);
  const [draftKeyword, setDraftKeyword] = useState(keyword);
  const [draftMinYear, setDraftMinYear] = useState(minYear);
  const [draftMaxYear, setDraftMaxYear] = useState(maxYear);
  const [draftPageSize, setDraftPageSize] = useState(pageSize);

  useEffect(() => {
    setDraftView(view);
    setDraftSort(sort);
    setDraftKeyword(keyword);
    setDraftMinYear(minYear);
    setDraftMaxYear(maxYear);
    setDraftPageSize(pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, sort, keyword, minYear, maxYear, pageSize]);

  const [dealer, setDealer] = useState(null);
  const [cars, setCars] = useState([]);
  const [total, setTotal] = useState(0);
  const [inventorySummary, setInventorySummary] = useState({
    activeInventory: 0,
    withMestimate: 0,
    belowEstimate: 0,
  });
  const [loading, setLoading] = useState(true);

  const API = process.env.REACT_APP_API_BASE;

  const writeParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === "" || v == null) next.delete(k);
      else next.set(k, String(v));
    });
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    writeParams({ page, pageSize });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        qs.set("page", String(page));
        qs.set("pageSize", String(pageSize));
        if (view) qs.set("view", view);
        if (sort) qs.set("sort", sort);
        if (keyword) qs.set("keyword", keyword);
        if (minYear) qs.set("minYear", minYear);
        if (maxYear) qs.set("maxYear", maxYear);

        const res = await fetch(`${API}/cars/dealer/${dealerId}/inventory?` + qs.toString());
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        setDealer(data.dealer || null);
        setCars(data.cars || []);
        setTotal(data.total || 0);
        setInventorySummary({
          activeInventory: data.inventorySummary?.activeInventory ?? 0,
          withMestimate: data.inventorySummary?.withMestimate ?? 0,
          belowEstimate: data.inventorySummary?.belowEstimate ?? 0,
        });
      } catch {
        setDealer((d) => d ?? null);
        setCars([]);
        setTotal(0);
        setInventorySummary({ activeInventory: 0, withMestimate: 0, belowEstimate: 0 });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API, dealerId, page, pageSize, view, sort, keyword, minYear, maxYear]);

  const applyFilters = () => {
    setPage(1);
    writeParams({
      view: draftView,
      sort: draftSort,
      keyword: draftKeyword,
      minYear: draftMinYear,
      maxYear: draftMaxYear,
      page: 1,
      pageSize: draftPageSize,
    });
    setPageSize(draftPageSize);
  };

  const clearFilters = () => {
    setDraftView("all");
    setDraftSort("recent");
    setDraftKeyword("");
    setDraftMinYear("");
    setDraftMaxYear("");
    setDraftPageSize(24);
    setPage(1);
    writeParams({
      view: "all",
      sort: "recent",
      keyword: "",
      minYear: "",
      maxYear: "",
      page: 1,
      pageSize: 24,
    });
    setPageSize(24);
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const viewLabel = useMemo(() => {
    if (view === "active") return "Active inventory";
    if (view === "sold") return "Sold inventory";
    return "All inventory";
  }, [view]);

  if (loading && !dealer) return <Spinner />;

  return (
    <div className="bg-slate-50 px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-7xl space-y-5">
        {dealer && (
          <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <div className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-blue-700">
                  Dealer storefront
                </div>
                <h1 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl">
                  {dealer.name || "Dealer Inventory"}
                </h1>
                <div className="mt-3 space-y-1 text-sm text-slate-600">
                  <div className="flex items-start gap-2">
                    <FaMapMarkerAlt className="mt-1 text-slate-400" />
                    <span>
                      {dealer.address}, {dealer.city}, {dealer.state} {dealer.zip}
                    </span>
                  </div>
                  {dealer.phone && (
                    <div className="flex items-center gap-2">
                      <FaPhoneAlt className="text-slate-400" />
                      <a href={`tel:${dealer.phone}`} className="font-medium text-blue-700 hover:text-blue-800">
                        {dealer.phone}
                      </a>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3 lg:min-w-[320px]">
                <Stat label="Active Inventory" value={inventorySummary.activeInventory.toLocaleString()} />
                <Stat label="With Mestimate" value={inventorySummary.withMestimate.toLocaleString()} />
                <Stat label="Below Estimate" value={inventorySummary.belowEstimate.toLocaleString()} />
              </div>
            </div>
          </section>
        )}

        <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-6">
            <Field label="View">
              <select
                value={draftView}
                onChange={(e) => setDraftView(e.target.value)}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              >
                <option value="all">All Listings</option>
                <option value="active">Active Only</option>
                <option value="sold">Sold Only</option>
              </select>
            </Field>

            <Field label="Sort">
              <select
                value={draftSort}
                onChange={(e) => setDraftSort(e.target.value)}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              >
                <option value="recent">Most Recent</option>
                <option value="price_asc">Price ↑</option>
                <option value="price_desc">Price ↓</option>
                <option value="year_desc">Year ↓</option>
                <option value="mileage_asc">Mileage ↑</option>
              </select>
            </Field>

            <Field label="Search">
              <input
                value={draftKeyword}
                onChange={(e) => setDraftKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="Search make or model"
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </Field>

            <Field label="Min Year">
              <input
                type="number"
                value={draftMinYear}
                onChange={(e) => setDraftMinYear(e.target.value)}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </Field>

            <Field label="Max Year">
              <input
                type="number"
                value={draftMaxYear}
                onChange={(e) => setDraftMaxYear(e.target.value)}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              />
            </Field>

            <Field label="Per Page">
              <select
                value={draftPageSize}
                onChange={(e) => setDraftPageSize(Number(e.target.value))}
                className="h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              >
                {[12, 24, 36, 48].map((sz) => (
                  <option key={sz} value={sz}>
                    {sz} / page
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-slate-600">
              {viewLabel} filtered by{" "}
              <span className="font-medium text-slate-900">{pageSize.toLocaleString()} per page</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={applyFilters}
                className="inline-flex h-11 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
              >
                Apply
              </button>
              <button
                onClick={clearFilters}
                className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Clear
              </button>
            </div>
          </div>
        </section>

        <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div>
            <div className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">Inventory results</div>
            <div className="mt-1 text-sm text-slate-600">
              Showing <span className="font-semibold text-slate-950">{cars.length.toLocaleString()}</span> of{" "}
              <span className="font-semibold text-slate-950">{total.toLocaleString()}</span> results
            </div>
          </div>
          <div className="text-sm text-slate-600">
            Page <span className="font-semibold text-slate-950">{page}</span> of{" "}
            <span className="font-semibold text-slate-950">{totalPages}</span>
          </div>
        </section>

        {cars.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {cars.map((car) => (
              <InventoryCard key={car.id} car={car} />
            ))}
          </div>
        )}

        <section className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
          <div className="text-sm text-slate-600">
            Page <span className="font-semibold text-slate-950">{page}</span> of{" "}
            <span className="font-semibold text-slate-950">{totalPages}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex h-11 items-center justify-center rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}

function InventoryCard({ car }) {
  const sold = !!car.soldDate;
  const image = car.imageUrl || "https://meshumimages.blob.core.windows.net/websiteimages/defaultCar.jpg";
  const title = [car.year, car.make, car.model, car.trim].filter(Boolean).join(" ");
  const location = [car.dealerCity, car.dealerState].filter(Boolean).join(", ");
  const badgeText = sold ? "Sold" : "Active";

  return (
    <article className="flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition hover:shadow-md">
      <div className="relative bg-slate-100">
        <img
          src={image}
          alt={`${car.year} ${car.make} ${car.model}`}
          className="h-52 w-full object-cover sm:h-48"
          loading="lazy"
        />
        <span
          className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-[11px] font-semibold shadow ${
            sold ? "bg-rose-600 text-white" : "bg-emerald-600 text-white"
          }`}
        >
          {badgeText}
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-4 p-5">
        <div>
          <div className="flex items-start justify-between gap-3">
            <h2 className="min-h-[3.5rem] text-base font-semibold leading-snug text-slate-950">{title}</h2>
          </div>
          <div className="mt-2 space-y-1 text-sm text-slate-600">
            <div>{typeof car.mileage === "number" ? `${car.mileage.toLocaleString()} miles` : "Mileage unavailable"}</div>
            {location && <div>{location}</div>}
          </div>
        </div>

        <div className="mt-auto flex items-end justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Price</div>
            <div className={`mt-1 text-2xl font-semibold ${sold ? "text-slate-700 line-through" : "text-slate-950"}`}>
              {typeof car.price === "number"
                ? car.price.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
                : "Price unavailable"}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link
              to={getVehiclePath(car)}
              className="inline-flex h-11 items-center justify-center rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
            >
              View details
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-slate-950">{value}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="mx-auto max-w-2xl rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-700">
        <FaCarSide className="text-xl" />
      </div>
      <h2 className="mt-4 text-xl font-semibold text-slate-950">No inventory found</h2>
      <p className="mt-2 text-sm leading-6 text-slate-600">
        Adjust the filters or switch the inventory view to find active or sold vehicles for this dealer.
      </p>
    </div>
  );
}
